import {
  MutationFunction,
  QueryKey,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query'
import { FieldValues, UseFormSetError } from 'react-hook-form'
import { GqlInputErrorException } from './gql'

type QueryKeyFn<TVariables> = (data: unknown, variables: TVariables) => QueryKey
type InvalidatableQueryKeys<TVariables> = Array<QueryKey | QueryKeyFn<TVariables>> | 'all'
type Callbacks<TData = unknown, TError = unknown, TVariables = unknown, TContext = unknown> = NonNullable<
  Pick<
    UseMutationOptions<TData, TError, TVariables, TContext>,
    'onMutate' | 'onError' | 'onSuccess' | 'onSettled'
  >
>

export const composeMutationCallbacks = <
  TData = unknown,
  TError = unknown,
  TVariables = unknown,
  TContext = unknown,
>(
  target: Omit<UseMutationOptions<TData, TError, TVariables, TContext>, 'mutationFn'> = {}, // we don't ever supply mutationFn in mutationOptions
  callbacks: Callbacks<TData, TError, TVariables, TContext>
): typeof target => ({
  ...target,
  onSuccess: (...args) => {
    if (callbacks.onSuccess) callbacks.onSuccess(...args) // Call for side effects
    return target?.onSuccess?.(...args)
  },
  onError: (...args) => {
    if (callbacks.onError) callbacks.onError(...args)
    return target?.onError?.(...args)
  },
  onMutate: (...args) => {
    if (callbacks.onMutate) callbacks.onMutate(...args)
    return target?.onMutate?.(...args)
  },
  onSettled: (...args) => {
    if (callbacks.onSettled) callbacks.onSettled(...args)
    return target?.onSettled?.(...args)
  },
})

/**
 * This hook merges the supplied mutation options with an onSuccess callback that invalidates the
 * supplied query keys. The returned mutation options can then be passed to `useMutation`
 */
export const useMutationOptionsWithInvalidation = <
  TData = unknown,
  TError = unknown,
  TVariables = unknown,
  TContext = unknown,
>(
  callbacks: Callbacks<TData, TError, TVariables, TContext> = {},
  queryKeys: InvalidatableQueryKeys<TVariables>
): typeof callbacks => {
  const queryClient = useQueryClient()

  const onSuccess: typeof callbacks.onSuccess = (data, variables): void => {
    if (queryKeys === 'all') {
      queryClient.invalidateQueries()
    } else {
      queryKeys.forEach((queryKey) => {
        const key = typeof queryKey === 'function' ? queryKey(data, variables) : queryKey
        queryClient.invalidateQueries(key)
      })
    }
  }
  return composeMutationCallbacks(callbacks, { onSuccess })
}

/**
 * This is a light wrapper around `useMutation`, adding a third argument that is an array of query keys
 * that will be invalidated in the onSuccess callback of the mutation. Use it as a convenient replacement
 * for `useMutation` whenever you have queries that need to be invalidated
 */
export const useMutationWithInvalidation = <
  TData = unknown,
  TError = unknown,
  TVariables = unknown,
  TContext = unknown,
>(
  mutationFn: MutationFunction<TData, TVariables>,
  callbacks: Callbacks<TData, TError, TVariables, TContext> = {},
  queryKeys: InvalidatableQueryKeys<TVariables>
): UseMutationResult<TData, TError, TVariables, TContext> => {
  const _mutationOptions: typeof callbacks = useMutationOptionsWithInvalidation(callbacks, queryKeys)
  return useMutation(mutationFn, _mutationOptions)
}

export const getGqlInputErrorHandler =
  <TFieldValues extends FieldValues>(
    setError: UseFormSetError<TFieldValues>,
    rootFields: Array<string> = []
  ) =>
  <TVariables extends Record<string, unknown>>(error: GqlInputErrorException<TVariables>) => {
    error.inputErrors.forEach(({ argument, message }) => {
      if (typeof argument === 'string' && rootFields.includes(argument)) {
        argument = `root.${argument}`
      }
      setError(argument as Parameters<typeof setError>[0], { message }, { shouldFocus: true })
    })
  }
